import { ChangeEvent, DragEvent, useEffect, useState } from "react";
import { useController, UseFormSetValue, useWatch } from "react-hook-form";
import { FC, useRef } from "react";
import { Upload } from "lucide-react";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";

interface Props {
	stepNumber: number;
	order: number;
	placeholder: string;
	control: any;
	error: boolean;
	upload: (file: File, uploadType: string) => void;
	uuid: string | null;
	done: boolean;
	progress: number;
	uploading: boolean;
	setValue: UseFormSetValue<BannerFormState>;
	imageProfile?: ImageProfile;
}

const ImageUpload: FC<Props> = ({
	stepNumber,
	order,
	placeholder,
	control,
	upload,
	uuid,
	uploading,
	setValue,
	progress,
	imageProfile
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);

	const bannerType = useWatch({ name: "bannerType", control });

	const givenAnswer = useWatch({
		name: `steps.${stepNumber - 1}.fields.${order - 1}.givenAnswer`,
		control
	});

	const isImageRequired =
		bannerType !== "videoBanner" || bannerType !== "promotionVideo";

	const {
		field: { onChange: uuidOnChange, value: uuidValue, ...inputProps }
	} = useController({
		name: `steps.${stepNumber - 1}.fields.${order - 1}.givenAnswer`,
		control,
		rules: { required: isImageRequired }
	});

	const {
		field: { onChange: fileNameOnChange }
	} = useController({
		name: `steps.${stepNumber - 1}.fields.${order - 1}.file`,
		control
	});

	const processFile = (file: File) => {
		const reader = new FileReader();

		reader.onload = (progressEvent: ProgressEvent<FileReader>) => {
			if (!progressEvent?.target) return;

			setValue(
				`steps.${stepNumber - 1}.fields.${order - 1}.preview`,
				progressEvent.target.result as string
			);

			upload(file, "images");
		};

		reader.readAsDataURL(file);
	};

	const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;
		if (!target?.files) return;
		processFile(target.files[0]);
	};

	const handleDragOver = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: DragEvent) => {
		e.preventDefault();
		setIsDragging(false);

		const file = e.dataTransfer.files[0];
		if (!file) return;

		const isValid = file.type === "image/jpeg" || file.type === "image/png";
		if (!isValid) {
			alert("Only JPG / PNG file types can be selected");
			return;
		}

		processFile(file);
	};

	const handleRemoveImage = () => {
		uuidOnChange("");
		setValue(`steps.${stepNumber - 1}.fields.${order - 1}.preview`, "");
		if (inputRef.current) inputRef.current.value = "";
	};

	useEffect(() => {
		if (!uuid) return;

		setValue(`steps.${stepNumber - 1}.fields.${order - 1}.givenAnswer`, uuid);
		uuidOnChange(uuid);
	}, [uuid]);

	const hasImage = !!(uuid || givenAnswer);

	return (
		<div className="max-w-[480px]">
			<input
				type="file"
				onChange={onFileSelected}
				accept="image/jpeg,image/png"
				{...inputProps}
				name={`steps.${stepNumber - 1}.fields.${order - 1}.file`}
				ref={inputRef}
				style={{ display: "none" }}
				aria-label="file-upload"
			/>

			{hasImage && !uploading ? (
				<>
					<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px]">
						<AzureImage
							uuid={(uuid || givenAnswer) as string}
							imageProfile={imageProfile || "retail-app-homepage-promotion"}
							style={{
								objectFit: "contain",
								width: "100%",
								height: "auto",
								maxHeight: "400px"
							}}
							alt="Uploaded image preview"
						/>
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
							onClick={() => inputRef?.current?.click()}
						>
							Change image
						</button>
						<button
							type="button"
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
							onClick={handleRemoveImage}
						>
							Remove
						</button>
					</div>
				</>
			) : (
				<div
					role="button"
					tabIndex={0}
					onClick={() => !uploading && inputRef?.current?.click()}
					onKeyDown={(e) => {
						if ((e.key === "Enter" || e.key === " ") && !uploading) {
							e.preventDefault();
							inputRef?.current?.click();
						}
					}}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					className={[
						"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
						isDragging
							? "border-primary bg-primary-lighter"
							: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50",
						uploading ? "pointer-events-none opacity-60" : ""
					].join(" ")}
				>
					{uploading ? (
						<>
							<div className="w-full max-w-[200px] h-2 rounded-full bg-muted overflow-hidden">
								<div
									className="h-full bg-primary rounded-full transition-all duration-300"
									style={{ width: `${progress}%` }}
								/>
							</div>
							<span className="text-sm text-muted-foreground">
								Uploading...
							</span>
						</>
					) : (
						<>
							<Upload className="h-6 w-6 text-muted-foreground" />
							<div>
								<p className="text-sm font-medium text-foreground">
									Drag image here or click to browse
								</p>
								<p className="text-xs text-muted-foreground mt-1">JPG or PNG</p>
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default ImageUpload;
